import { http } from '@frontend/fetch';
import { PhoneRecordsSchemaService, SchemaIO, VoicemailSchemaService } from '@frontend/schema';
import { sentry } from '@frontend/tracking';
import {
  VoicemailPayload,
  VoicemailResponse,
  OnReadParams,
  DeleteVoicemailParams,
  DownloadVoicemailResponse,
  ToggleReadResponse,
  GetVoicemailsTypes,
  SetVoicemailViewStatusTypes,
  DeleteVoicemailTypes,
  ListVoicemailBox,
  ForwardVoicemailTypes,
  GetVoicemailType,
  ListVoicemailMessages,
} from './types';

const baseEndpoint = 'desktop/v1/voicemails';

export const getVoicemails = async (payload: VoicemailPayload, url?: string): Promise<VoicemailResponse> => {
  const { limit = 0, skip = 0, departmentIds = [], isGeneral, workstationSlug, departmentsEnabled } = payload;
  const departmentIdsParams = departmentIds.length ? `&departmentID=${departmentIds.join('&departmentID=')}` : '';
  const mailboxParams = !departmentsEnabled ? `&mailbox=${isGeneral ? 'general' : 'currentWorkstation'}` : '';
  const workstationSlugParams = `&workstationSlug=${!!workstationSlug ? workstationSlug : 'none'}`;

  const { data, meta } = await http.get<VoicemailResponse>(
    url ||
      `${baseEndpoint}?limit=${limit}&skip=${skip}&orderBy=createdAt${departmentIdsParams}${workstationSlugParams}${mailboxParams}`
  );
  return { data: skip === 0 ? data.slice(0, limit) : data, meta };
};

export const markVoicemailAsRead = async ({ mailboxId, messageId, markedRead }: OnReadParams) =>
  http.put<ToggleReadResponse>(`${baseEndpoint}/read/mailboxid/${mailboxId}/messageid/${messageId}`, { markedRead });

export const deleteVoicemail = async ({ mailboxId, messageId }: DeleteVoicemailParams) =>
  http.delete<Record<string, never>>(`${baseEndpoint}/mailboxid/${mailboxId}/messageid/${messageId}`);

export const downloadVoicemail = async (url: string) =>
  http.get<DownloadVoicemailResponse>(url, { responseType: 'blob' });

//------------schema endpoints here--------------

export const listVoicemails = (req: GetVoicemailsTypes['input']): Promise<GetVoicemailsTypes['output']> => {
  return VoicemailSchemaService.ListVoicemails(req);
};

// Track consecutive calls to detect potential issues
let consecutiveCalls: Array<{ timestamp: number; req: any }> = [];
const CALL_THRESHOLD = 5;
const TIME_WINDOW = 1000;

export const setVoicemailViewStatus = (req: SetVoicemailViewStatusTypes['input']) => {
  const now = Date.now();

  // Add current call to tracking
  consecutiveCalls.push({ timestamp: now, req });

  // Remove calls older than the time window
  consecutiveCalls = consecutiveCalls.filter((call) => now - call.timestamp <= TIME_WINDOW);

  // Check if we have too many calls in the time window
  if (consecutiveCalls.length >= CALL_THRESHOLD && sentry) {
    sentry.log({
      message: 'Multiple setVoicemailViewStatus calls detected',
      category: 'voicemail.api',
      team: 'fe-phones',
      data: {
        callCount: consecutiveCalls.length,
        timeWindow: TIME_WINDOW,
        voicemailIds: consecutiveCalls.map((call) => call.req.voicemailId),
        statuses: consecutiveCalls.map((call) => call.req.status),
      },
    });

    // Reset the counter after logging to avoid spam
    consecutiveCalls = [];
  }

  return VoicemailSchemaService.SetVoicemailViewStatus(req);
};

export const deleteVoicemailMedia = (req: DeleteVoicemailTypes['input']) => {
  return VoicemailSchemaService.DeleteVoicemail(req);
};

export const forwardVoicemail = (req: ForwardVoicemailTypes['input']) => {
  return VoicemailSchemaService.ForwardVoicemailMessage(req);
};

export const listMultiVoicemailMessages = (req: ListVoicemailMessages['input']) => {
  return PhoneRecordsSchemaService.ListVoicemailMessages(req);
};

export const listVoicemailBox = (req: ListVoicemailBox['input']) => {
  return PhoneRecordsSchemaService.ListVoicemailBoxes(req);
};

export const tagVoicemail = (req: SchemaIO<typeof PhoneRecordsSchemaService.ApplyVoicemailMessageTags>['input']) => {
  return PhoneRecordsSchemaService.ApplyVoicemailMessageTags(req);
};

export const untagVoicemail = (req: SchemaIO<typeof PhoneRecordsSchemaService.RemoveVoicemailMessageTags>['input']) => {
  return PhoneRecordsSchemaService.RemoveVoicemailMessageTags(req);
};

export const getVoicemail = (req: GetVoicemailType['input']) => {
  return VoicemailSchemaService.GetVoicemail(req);
};

export const getVoicemailWithTenantAuth = (req: GetVoicemailType['input']) => {
  return VoicemailSchemaService.GetVoicemailWithTenantAuth(req);
};
